import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  Contract,
  ContractDetail,
  ContractHistory,
  ContractListQuery,
  ContractSummary,
  ContractStatusUpdateRequest,
  CreateContractRequest,
  EditContractRequest,
  RenewContractRequest,
  SendSignatureRequest,
  ListQuery,
  Job,
  Documentable,
  PdfLink,
  ExportLink,
} from '@/types'

// 1:1 with Schema_Specification Section 4.8 (contracts / 2.8.x).
export const contractsApi = {
  // 2.8.1
  list: (q: ContractListQuery = {}) =>
    apiClient
      .get<ApiList<Contract>>('/contracts', { params: withListDefaults(q) })
      .then(r => r.data),

  // 2.8.1 - list KPI summary (active / pending_signature / to_renew / expired
  // plus annual amounts). Tenant-wide (feedback #53), not page-local.
  summary: () =>
    apiClient
      .get<ApiResponse<ContractSummary>>('/contracts/summary')
      .then(r => r.data),

  // List export (feedback #55). Honours the current search/status/end-date
  // filters; returns a signed download link named Contract_list_[YYYY-MM-DD].
  export: (q: ContractListQuery & { format?: 'xlsx' | 'csv' } = {}) =>
    apiClient
      .get<ApiResponse<ExportLink>>('/contracts/export', { params: q })
      .then(r => r.data),

  // Service-category options for the contract category multi-select (#56) and
  // detail display (#61). This module-specific projection is retained for
  // contract screens even though the shared category index is authenticated.
  serviceCategoryOptions: () =>
    apiClient
      .get<
        ApiResponse<{ id: string; name: string }[]>
      >('/contracts/service-category-options')
      .then(r => r.data),

  // 2.8.5
  get: (id: string) =>
    apiClient
      .get<ApiResponse<ContractDetail>>(`/contracts/${id}`)
      .then(r => r.data),

  // 2.8.2
  create: (body: CreateContractRequest) =>
    apiClient.post<ApiResponse<Contract>>('/contracts', body).then(r => r.data),

  // 2.8.3
  update: (id: string, body: EditContractRequest) =>
    apiClient
      .put<ApiResponse<Contract>>(`/contracts/${id}`, body)
      .then(r => r.data),

  // 2.8.4
  renew: (id: string, body: RenewContractRequest) =>
    apiClient
      .post<ApiResponse<Contract>>(`/contracts/${id}/renew`, body)
      .then(r => r.data),

  // 2.8.x - status workflow (activate / suspend / terminate).
  updateStatus: (id: string, body: ContractStatusUpdateRequest) =>
    apiClient
      .put<ApiResponse<Contract>>(`/contracts/${id}/status`, body)
      .then(r => r.data),

  // 2.8.6
  history: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<ContractHistory>>(`/contracts/${id}/history`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  // 2.8.7
  interventions: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Job>>(`/contracts/${id}/interventions`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  // 2.8.8 - send for e-signature (YouSign).
  sendSignature: (id: string, body: SendSignatureRequest) =>
    apiClient
      .post<ApiResponse<Contract>>(`/contracts/${id}/send-signature`, body)
      .then(r => r.data),

  // 2.8.9
  pdf: (id: string) =>
    apiClient
      .get<ApiResponse<PdfLink>>(`/contracts/${id}/pdf`)
      .then(r => r.data),

  // 2.8.10 - list attachments (paginated).
  documents: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<Documentable>>(`/contracts/${id}/documents`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  // 2.8.10 - add documents (multipart).
  uploadDocument: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    form.append('doc_type', 'contract')
    return apiClient
      .post<ApiResponse<Documentable>>(`/contracts/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },

  // Attach an existing client/library file to the contract (feedback #59).
  attachExistingDocument: (
    id: string,
    fileId: string,
    docType: 'client' | 'library'
  ) =>
    apiClient
      .post<ApiResponse<Documentable>>(`/contracts/${id}/documents/attach`, {
        file_id: fileId,
        doc_type: docType,
      })
      .then(r => r.data),

  renameDocument: (id: string, documentId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<Documentable>
      >(`/contracts/${id}/documents/${documentId}/rename`, { name })
      .then(r => r.data),

  // 2.8.10 - delete an attachment.
  deleteDocument: (id: string, documentId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/contracts/${id}/documents/${documentId}`)
      .then(r => r.data),

  // 2.8.11
  remove: (id: string) =>
    apiClient.delete<ApiResponse<null>>(`/contracts/${id}`).then(r => r.data),
}

export default contractsApi
